import { capitalize } from 'lodash-es';
import { BoxV2 as Box, Flex } from 'portal-commons';
import { FunctionComponent, MouseEvent, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  faEllipsisVertical,
  faHourglass,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableCell, TableRow } from '@material-ui/core';

import { remapTCRStatus, formatTitleCase } from '../utils';
import { CampaignListItem } from '@/apis/core/types';
import { Menu } from '@/components';
import { convertTimeWithTimezone } from '@/utils/time';

export enum ActionType {
  Accept = 'accept',
  Decline = 'decline',
}

interface RcsCampaignsByCspsListingRowProps {
  campaign: CampaignListItem;
  onActionClick?: (type: ActionType) => void;
  permissions?: {
    accept?: boolean;
    decline?: boolean;
  };
}

const RcsCampaignsByCspsListingRow: FunctionComponent<
  RcsCampaignsByCspsListingRowProps
> = ({ campaign, onActionClick, permissions }) => {
  const history = useHistory();
  const location = useLocation();
  const [menuElement, setMenuElement] = useState<HTMLElement | null>(null);

  const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuElement(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuElement(null);
  };

  const getCampaignStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'pending':
        return <FontAwesomeIcon icon={faHourglass} fontSize={12} />;
      case 'accepted':
        return <FontAwesomeIcon icon={faCheck} fontSize={12} />;
      default:
        return null;
    }
  };

  const statusIcon = (sharingStatus: string) => {
    return (
      <Flex sx={{ alignItems: 'center', gap: 'xs' }}>
        <div>
          {getCampaignStatusIcon(campaign.sharingStatus?.toLowerCase())}
        </div>
        <div>{capitalize(sharingStatus)}</div>
      </Flex>
    );
  };

  return (
    <TableRow
      onClick={() =>
        history.push({
          pathname: `/rcs-csp-campaigns/${campaign.uid}`,
          state: {
            goBackPage: `${location.pathname}${location.search ?? ''}`,
          },
        })
      }
      data-testid="RcsCampaignsByCspsListingRow"
    >
      <TableCell align="left" data-testid="RcsCampaignsByCspsListingRowUid">
        {campaign.uid}
      </TableCell>
      {/* <TableCell
        style={{ minWidth: 128 }}
        align="left"
        data-testid="RcsCampaignsByCspsListingRowDisplayName"
      >
        {campaign.displayName}
      </TableCell> */}
      <TableCell
        align="left"
        data-testid="RcsCampaignsByCspsListingRowBrandUid"
      >
        {campaign.brandUid}
      </TableCell>

      <TableCell
        align="left"
        data-testid="RcsCampaignsByCspsListingRowBrandName"
        style={{
          maxWidth: 200,
          wordBreak: 'break-all',
        }}
      >
        {campaign.brandName}
      </TableCell>
      <TableCell align="left" data-testid="RcsCampaignsByCspsListingRowUseCase">
        {campaign.usecase}
      </TableCell>
      <TableCell
        align="left"
        data-testid="RcsCampaignsByCspsListingRowBillingCategory"
      >
        {formatTitleCase(campaign.billingCategory)}
      </TableCell>
      <TableCell
        align="left"
        data-testid="RcsCampaignsByCspsListingRowCspName"
        style={{
          maxWidth: 200,
          wordBreak: 'break-all',
        }}
      >
        {campaign.cspName}
      </TableCell>
      <TableCell
        align="left"
        data-testid="RcsCampaignsByCspsListingRowDownstreamCnpName"
        style={{
          maxWidth: 200,
          wordBreak: 'break-all',
        }}
      >
        {campaign.downstreamCnpName}
      </TableCell>

      <TableCell
        align="left"
        data-testid="RcsCampaignsByCspsListingRowSharedOnDate"
      >
        {campaign.sharedOnDate
          ? convertTimeWithTimezone(campaign.sharedOnDate, 'MM/DD/YYYY')
          : '-'}
      </TableCell>
      <TableCell align="left" data-testid="RcsCampaignsByCspsListingRowStatus">
        {remapTCRStatus(campaign.status)}
      </TableCell>
      <TableCell
        align="left"
        data-testid="RcsCampaignsByCspListingRowSharingStatus"
      >
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          {statusIcon(campaign.sharingStatus || '')}
          <Flex sx={{ alignItems: 'center' }}>
            {campaign.sharingStatus === 'PENDING' &&
              campaign.status !== 'EXPIRED' && (
                <Box
                  sx={{
                    cursor: 'pointer',
                    pr: '10px',
                    pl: '16px',
                    width: '44px',
                    '&:hover svg': {
                      color: 't.orange500',
                    },
                  }}
                  onClick={handleMenuClick}
                  data-testid="rcsCampaignsByCspsListingRowActionMenu"
                >
                  <FontAwesomeIcon icon={faEllipsisVertical} fontSize="16" />
                  <Menu
                    open={!!menuElement}
                    anchorEl={menuElement}
                    onClose={handleMenuClose}
                    onItemClick={(value: string) =>
                      onActionClick?.(value as ActionType)
                    }
                    sx={{ minWidth: 60 }}
                    data-testid="menuList"
                  >
                    <Flex sx={{ flexDirection: 'column' }}>
                      <Menu.MenuItem
                        value={ActionType.Accept}
                        disabled={permissions?.accept === false}
                      >
                        Accept
                      </Menu.MenuItem>
                      <Menu.MenuItem
                        value={ActionType.Decline}
                        disabled={permissions?.decline === false}
                      >
                        Decline
                      </Menu.MenuItem>
                    </Flex>
                  </Menu>
                </Box>
              )}
          </Flex>
        </Flex>
      </TableCell>
    </TableRow>
  );
};

export default RcsCampaignsByCspsListingRow;
